import { useEffect } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { toast } from 'sonner';
import { isSessionActive, subscribe } from '@/lib/pwaUpdateGate';

/**
 * Registers the service worker in "prompt" mode and surfaces an in-app
 * "Update Available" toast. Crucially, an update is NEVER applied automatically:
 * the page only reloads when the user taps Refresh. While a live recording or
 * voice conversation is in progress, the prompt is deferred until the session
 * ends so a tab switch can't trigger a mid-session reload.
 */
export const usePWAUpdate = () => {
  useEffect(() => {
    let updateSW: ((reloadPage?: boolean) => Promise<void>) | undefined;
    let registration: ServiceWorkerRegistration | undefined;
    let updateReady = false;
    let toastShown = false;

    const showUpdateToast = () => {
      if (toastShown) return;
      toastShown = true;
      toast('Update Available', {
        description: 'A new version is available. Tap to refresh.',
        action: {
          label: 'Refresh',
          onClick: () => updateSW?.(true),
        },
        duration: Infinity,
      });
    };

    // Only prompt when an update is ready AND no critical session is running.
    const maybePrompt = () => {
      if (!updateReady) return;
      if (isSessionActive()) return;
      showUpdateToast();
    };

    updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        updateReady = true;
        maybePrompt();
      },
      onRegisteredSW(_swUrl, reg) {
        registration = reg;
      },
    });

    // Re-evaluate when the last active session ends (deferred update may exist).
    const unsubscribe = subscribe(maybePrompt);

    // Background update checks — skipped while a session is active so we never
    // pull in (or surface) a new worker mid-conversation.
    const checkForUpdates = () => {
      if (isSessionActive()) return;
      registration?.update().catch(() => {});
    };

    const interval = setInterval(checkForUpdates, 60 * 1000);

    const onFocus = () => checkForUpdates();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkForUpdates();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      unsubscribe();
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
};
